/**
 * Authentication Repository
 * Handles Firebase Authentication, Firestore user data, and Provider API key management
 */

import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  getIdToken
} from 'firebase/auth';
import {
  doc,
  getDoc
} from 'firebase/firestore';
import { auth, db, googleProvider } from '../../../../config/firebaseConfig';

export class AuthRepository {
  constructor(restClient, logger) {
    this.restClient = restClient;
    // Create a no-op logger if none provided
    this.logger = logger || {
      info: () => {},
      error: () => {},
      warn: () => {}
    };
    this.currentUser = null;
    this.auth = auth; // Expose auth instance

    this.setupAuthStateListener();
  }

  /**
   * Sign in user with email and password
   */
  async signInWithEmail(email, password) {
    try {
      // Check if Firebase auth is properly initialized
      if (!auth) {
        throw new Error('Firebase authentication is not properly configured. Please check your environment variables.');
      }



      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const whitelistResult = await this.checkUserWhitelist(user.email);

      if (!whitelistResult.isWhitelisted) {
        await signOut(auth);
        return {
          success: false,
          error: 'Thank you for your interest in our product! However, you are not currently included in our beta version. Please contact us if you believe this is an error or if you are interested to try',
          user: null,
          apiKey: null
        };
      }

      let apiKey = null;
      let apiKeyError = null;
      let userData = null;

      try {
        const result = await this.getUserData(user.email);
        apiKey = result.apiKey;
        userData = result.userData;
        if (apiKey) {
          localStorage.setItem('PROVIDER_API_KEY', apiKey);
        }
      } catch (error) {
        apiKeyError = error.message;

      }

      const transformedUser = this.transformFirebaseUser(user);
      localStorage.setItem('user_info', JSON.stringify(transformedUser));



      return {
        success: true,
        user: transformedUser,
        apiKey: apiKey,
        apiKeyError: apiKeyError,
        userData: userData
      };

    } catch (error) {


      return {
        success: false,
        error: error.message,
        user: null,
        apiKey: null
      };
    }
  }

  /**
   * Sign in user with Google OAuth
   */
  async signInWithGoogle() {
    try {


      googleProvider.addScope('profile');
      googleProvider.addScope('email');

      const userCredential = await signInWithPopup(auth, googleProvider);
      const user = userCredential.user;

      // Extract email from user object (handle cases where user.email is null)
      let emailForValidation = user.email;
      if (!emailForValidation && user.providerData && user.providerData.length > 0) {
        for (const provider of user.providerData) {
          if (provider.email) {
            emailForValidation = provider.email;
            break;
          }
        }
      }

      const whitelistResult = await this.checkUserWhitelist(emailForValidation);

      if (!whitelistResult.isWhitelisted) {
        await signOut(auth);
        return {
          success: false,
          error: 'Thank you for your interest in our product! However, you are not currently included in our beta version. Please contact us if you believe this is an error or if you are interested to try',
          user: null,
          apiKey: null
        };
      }

      let apiKey = null;
      let apiKeyError = null;
      let userData = null;

      try {
        const result = await this.getUserData(emailForValidation);
        apiKey = result.apiKey;
        userData = result.userData;
        if (apiKey) {
          localStorage.setItem('PROVIDER_API_KEY', apiKey);
        }
      } catch (error) {
        apiKeyError = error.message;

      }

      const transformedUser = this.transformFirebaseUser(user);
      localStorage.setItem('user_info', JSON.stringify(transformedUser));



      return {
        success: true,
        user: transformedUser,
        apiKey: apiKey,
        apiKeyError: apiKeyError,
        userData: userData
      };

    } catch (error) {


      return {
        success: false,
        error: error.message,
        user: null,
        apiKey: null
      };
    }
  }

  /**
   * Sign out current user and cleanup session
   */
  async signOut() {
    try {
      this.currentUser = null;

      // Clear all storage data
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear any cookies (if they exist)
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });

      await signOut(auth);


      return { success: true };

    } catch (error) {


      this.currentUser = null;
      
      // Emergency cleanup - still clear all storage even if Firebase signOut fails
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear any cookies (if they exist)
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Get current API key from localStorage (cached)
   */
  getCurrentApiKey() {
    return localStorage.getItem('PROVIDER_API_KEY');
  }

  /**
   * Validate current authentication token
   */
  async validateToken() {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { valid: false, error: 'No authenticated user' };
      }

      const idToken = await getIdToken(currentUser, true);
      return { valid: true, token: idToken };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }

  /**
   * Wait for Firebase auth state to be fully established with valid token
   */
  async waitForAuthState(expectedEmail, maxWaitTime = 5000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const checkAuthState = async () => {
        const currentUser = auth.currentUser;

        if (!currentUser) {
          if (Date.now() - startTime > maxWaitTime) {
            reject(new Error('Timeout waiting for auth state'));
            return;
          }
          setTimeout(checkAuthState, 100);
          return;
        }

        // Extract email from user object (handle cases where currentUser.email is null)
        let actualEmail = currentUser.email;
        if (!actualEmail && currentUser.providerData && currentUser.providerData.length > 0) {
          for (const provider of currentUser.providerData) {
            if (provider.email) {
              actualEmail = provider.email;
              break;
            }
          }
        }

        if (actualEmail !== expectedEmail) {
          reject(new Error(`Email mismatch: expected ${expectedEmail}, got ${actualEmail}`));
          return;
        }

        try {
          const idToken = await getIdToken(currentUser, true);
          resolve(idToken);
        } catch (error) {
          if (Date.now() - startTime > maxWaitTime) {
            reject(new Error(`Timeout getting ID token: ${error.message}`));
            return;
          }
          setTimeout(checkAuthState, 100);
        }
      };

      checkAuthState();
    });
  }

  /**
   * Check if user email exists in whitelist (users collection)
   */
  async checkUserWhitelist(userEmail) {
    try {


      if (!userEmail) {
        throw new Error('User email is required for whitelist validation');
      }

      await this.waitForAuthState(userEmail);

      if (!db) {
        throw new Error('101 Error: instance is not available');
      }

      const currentUser = auth.currentUser;

      if (!currentUser) {

        throw new Error('User must be authenticated to perform whitelist validation');
      }

      // Extract email from user object (handle cases where currentUser.email is null)
      let authenticatedEmail = currentUser.email;
      if (!authenticatedEmail && currentUser.providerData && currentUser.providerData.length > 0) {
        for (const provider of currentUser.providerData) {
          if (provider.email) {
            authenticatedEmail = provider.email;
            break;
          }
        }
      }

      if (authenticatedEmail !== userEmail) {

        throw new Error(`Access denied: Cannot validate whitelist for ${userEmail} while authenticated as ${authenticatedEmail}`);
      }

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Whitelist check timed out after 10 seconds')), 10000)
      );

      const userDocRef = doc(db, 'users', userEmail);
      const userDocPromise = getDoc(userDocRef);
      const userDoc = await Promise.race([userDocPromise, timeoutPromise]);

      if (!userDoc.exists()) {

        return {
          isWhitelisted: false,
          message: 'Thank you for your interest in our product! However, you are not currently included in our beta version. Please contact us if you believe this is an error or if you are interested to try'
        };
      }



      return {
        isWhitelisted: true,
        message: 'User is whitelisted'
      };

    } catch (error) {


      if (error.message.includes('Missing or insufficient permissions') ||
          error.code === 'permission-denied' ||
          error.message.includes('permission-denied')) {

        throw new Error('Access denied. Please check security configuration.');
      }

      if (error.message.includes('timed out') || error.message.includes('network')) {
        throw new Error('Network error during whitelist validation. Please check your connection and try again.');
      }

      if (error.message.includes('authenticated') || error.message.includes('auth')) {
        throw new Error('Authentication error during whitelist validation. Please sign in again.');
      }

      throw new Error(`Whitelist validation failed: ${error.message}`);
    }
  }

  /**
   * Get user's Provider API key from Firestore
   */
  async getUserApiKey(userEmail) {
    try {


      if (!userEmail) {
        throw new Error('User email is required for API key retrieval');
      }

      if (!db) {
        throw new Error('101 Error: instance is not available');
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {

        throw new Error('User must be authenticated to access Database. Please sign in first.');
      }

      // Extract email from user object (handle cases where currentUser.email is null)
      let authenticatedEmail = currentUser.email;
      if (!authenticatedEmail && currentUser.providerData && currentUser.providerData.length > 0) {
        for (const provider of currentUser.providerData) {
          if (provider.email) {
            authenticatedEmail = provider.email;
            break;
          }
        }
      }

      if (authenticatedEmail !== userEmail) {
        this.logger.warn('Email mismatch detected:', {
          authenticatedUserExtracted: authenticatedEmail,
          requestedEmail: userEmail
        });
        throw new Error(`Access denied: Cannot access document for ${userEmail} while authenticated as ${authenticatedEmail}`);
      }

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Database request timed out after 10 seconds')), 10000)
      );

      const userDocRef = doc(db, 'users', userEmail);
      const userDocPromise = getDoc(userDocRef);
      const userDoc = await Promise.race([userDocPromise, timeoutPromise]);

      if (!userDoc.exists()) {
        throw new Error(`User document not found for email: ${userEmail}. Please ensure the user document exists in the 'users' collection.`);
      }

      const userData = userDoc.data();

      if (!userData.hasOwnProperty('api_key')) {
        this.logger.warn('API key field missing in user document:', {
          email: userEmail,
          availableFields: Object.keys(userData)
        });
        throw new Error(`API key field not found in user document for: ${userEmail}. Please add an 'api_key' field to the user document.`);
      }

      const apiKey = userData.api_key;

      if (!apiKey || apiKey.trim() === '') {
        this.logger.warn('API key is empty or null:', {
          email: userEmail
        });
        throw new Error(`API key is empty for user: ${userEmail}. Please set a valid API key value in your Database document.`);
      }



      return apiKey;

    } catch (error) {


      if (error.message.includes('Missing or insufficient permissions')) {

        throw new Error(`Database permission denied: Unable to access user document. Please check Database security rules.`);
      }

      throw new Error(`API key retrieval failed: ${error.message}`);
    }
  }

  /**
   * Get user data including API key and first_login status
   */
  async getUserData(userEmail) {
    try {

      if (!userEmail) {
        throw new Error('User email is required for user data retrieval');
      }

      if (!db) {
        throw new Error('101 Error: instance is not available');
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to access Database. Please sign in first.');
      }

      // Extract email from user object (handle cases where currentUser.email is null)
      let authenticatedEmail = currentUser.email;
      if (!authenticatedEmail && currentUser.providerData && currentUser.providerData.length > 0) {
        for (const provider of currentUser.providerData) {
          if (provider.email) {
            authenticatedEmail = provider.email;
            break;
          }
        }
      }

      if (authenticatedEmail !== userEmail) {
        this.logger.warn('Email mismatch detected:', {
          authenticatedUserExtracted: authenticatedEmail,
          requestedEmail: userEmail
        });
        throw new Error(`Access denied: Cannot access document for ${userEmail} while authenticated as ${authenticatedEmail}`);
      }

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Database request timed out after 10 seconds')), 10000)
      );

      const userDocRef = doc(db, 'users', userEmail);
      const userDocPromise = getDoc(userDocRef);
      const userDoc = await Promise.race([userDocPromise, timeoutPromise]);

      if (!userDoc.exists()) {

        throw new Error(`User document not found for email: ${userEmail}. Please ensure the user document exists in the 'users' collection.`);
      }

      const userData = userDoc.data();

      if (!userData.hasOwnProperty('api_key')) {
        this.logger.warn('API key field missing in user document:', {
          email: userEmail,
          availableFields: Object.keys(userData)
        });
        throw new Error(`API key field not found in user document for: ${userEmail}. Please add an 'api_key' field to the user document.`);
      }

      const apiKey = userData.api_key;

      if (!apiKey || apiKey.trim() === '') {
        this.logger.warn('API key is empty or null:', {
          email: userEmail
        });
        throw new Error(`API key is empty for user: ${userEmail}. Please set a valid API key value in your Database document.`);
      }



      return {
        apiKey: apiKey,
        userData: userData
      };

    } catch (error) {


      if (error.message.includes('Missing or insufficient permissions')) {

        throw new Error(`Database permission denied: Unable to access user document. Please check Database security rules.`);
      }

      throw new Error(`User data retrieval failed: ${error.message}`);
    }
  }

  /**
   * Set up authentication state listener
   */
  setupAuthStateListener() {
    // Check if Firebase auth is properly initialized
    if (!auth) {

      return;
    }

    try {
      onAuthStateChanged(auth, async (user) => {
        if (user) {


          this.currentUser = this.transformFirebaseUser(user);

          localStorage.setItem('user_info', JSON.stringify(this.currentUser));

          const emailToStore = this.currentUser.email || '';
          const nameToStore = this.currentUser.displayName || this.currentUser.email?.split('@')[0] || 'User';

          localStorage.setItem('userEmail', emailToStore);
          localStorage.setItem('userName', nameToStore);
          localStorage.setItem('userPhotoURL', this.currentUser.photoURL || '');

          // 🔧 FIX: Check if we already have an API key in localStorage first
          // This prevents logout on refresh when we already have a valid API key
          const existingApiKey = localStorage.getItem('PROVIDER_API_KEY');
          
          if (!existingApiKey) {
            // Only try to retrieve from Firestore if we don't have an API key locally
            try {


              const apiKey = await this.getUserApiKey(user.email);
              if (apiKey) {
                localStorage.setItem('PROVIDER_API_KEY', apiKey);

              }
            } catch (apiKeyError) {


              // Don't remove API key from localStorage if we failed to get it from Firestore
              // The user might have a valid API key already stored
              // Only log the error for debugging purposes


            }
          } else {

          }

        } else {


          this.currentUser = null;

          localStorage.removeItem('user_info');
          localStorage.removeItem('PROVIDER_API_KEY');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('userName');
          localStorage.removeItem('userPhotoURL');
        }
      });
    } catch (error) {

    }
  }

  /**
   * Transform Firebase user to our user format
   */
  transformFirebaseUser(firebaseUser) {
    let email = firebaseUser.email;

    if (!email && firebaseUser.providerData && firebaseUser.providerData.length > 0) {
      for (const provider of firebaseUser.providerData) {
        if (provider.email) {
          email = provider.email;
          break;
        }
      }
    }

    return {
      uid: firebaseUser.uid,
      email: email,
      displayName: firebaseUser.displayName || '',
      photoURL: firebaseUser.photoURL || '',
      emailVerified: firebaseUser.emailVerified,
    };
  }

  /**
   * Transform Firebase auth errors to user-friendly messages
   */
  transformAuthError(error) {
    const errorCode = error.code;

    switch (errorCode) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return 'Invalid email or password';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters';
      case 'auth/invalid-email':
        return 'Please enter a valid email address';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled';
      case 'auth/popup-blocked':
        return 'Pop-up was blocked. Please allow pop-ups and try again';
      default:

        return error.message || 'An authentication error occurred';
    }
  }
}
